import { authApi } from './adapters/api';
import httpClient from './adapters/httpClient';

// Session Timeout in Millisekunden (1 Minute zum Testen, spaeter auf 5 * 60 * 1000 aendern)
const SESSION_TIMEOUT_MS = 1 * 300 * 1000;
// Session-Pruefung alle 10 Sekunden (fuer verschiedene Browser)
const SESSION_CHECK_INTERVAL_MS = 10 * 1000;

let inactivityTimer = null;
let sessionCheckInterval = null;
let currentSessionId = null;
let activityListenersRegistered = false;

// Auth Service mit Session-Handling
const authService = {
  login: async (username, password) => {
    const response = await authApi.login(username, password);
    const { token, username: user, sessionId } = response;

    localStorage.setItem('token', token);
    localStorage.setItem('username', user);
    localStorage.setItem('sessionId', sessionId);
    currentSessionId = sessionId;

    // Starte Timer und registriere Activity-Events
    authService.startInactivityTimer();
    authService.startSessionCheck();
    authService.registerActivityListeners();

    return { token, username: user, sessionId };
  },

  logout: async () => {
    authService.stopAllTimers();

    try {
      await httpClient('/api/auth/logout', { method: 'POST' });
    } catch (error) {
      // Ignoriere Fehler beim Logout
    }

    localStorage.removeItem('token');
    localStorage.removeItem('username');
    localStorage.removeItem('sessionId');
    currentSessionId = null;
  },

  isAuthenticated: () => {
    return !!localStorage.getItem('token') && !!localStorage.getItem('sessionId');
  },

  getToken: () => localStorage.getItem('token'),
  getSessionId: () => localStorage.getItem('sessionId'),
  getUsername: () => localStorage.getItem('username'),

  // Prueft ob Session noch gueltig ist (fuer verschiedene Browser)
  checkSession: async () => {
    if (!authService.isAuthenticated()) return;

    try {
      await httpClient('/api/auth/heartbeat', { method: 'POST' });
    } catch (error) {
      if (error.status === 401) {
        console.log('Session invalid - kicked by server');
        authService.handleSessionExpired('kicked');
      }
    }
  },

  // Registriert Aktivitaet
  registerActivity: () => {
    authService.startInactivityTimer();
  },

  // Einmalige Registrierung der Activity-Events
  registerActivityListeners: () => {
    if (activityListenersRegistered) return;
    activityListenersRegistered = true;

    const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    activityEvents.forEach(event => {
      window.addEventListener(event, () => {
        if (authService.isAuthenticated()) {
          authService.registerActivity();
        }
      }, { passive: true });
    });
  },

  // Startet den Inactivity-Timer
  startInactivityTimer: () => {
    if (inactivityTimer) {
      clearTimeout(inactivityTimer);
    }

    inactivityTimer = setTimeout(() => {
      console.log('Session timeout due to inactivity');
      authService.handleSessionExpired('timeout');
    }, SESSION_TIMEOUT_MS);
  },

  // Startet regelmaessige Session-Pruefung
  startSessionCheck: () => {
    if (sessionCheckInterval) {
      clearInterval(sessionCheckInterval);
    }

    sessionCheckInterval = setInterval(() => {
      authService.checkSession();
    }, SESSION_CHECK_INTERVAL_MS);
  },

  // Stoppt alle Timer
  stopAllTimers: () => {
    if (inactivityTimer) {
      clearTimeout(inactivityTimer);
      inactivityTimer = null;
    }
    if (sessionCheckInterval) {
      clearInterval(sessionCheckInterval);
      sessionCheckInterval = null;
    }
    activityListenersRegistered = false;
  },

  // Handler fuer Session-Ablauf
  handleSessionExpired: (reason = 'expired') => {
    authService.stopAllTimers();
    localStorage.removeItem('token');
    localStorage.removeItem('username');
    localStorage.removeItem('sessionId');
    currentSessionId = null;

    if (reason === 'timeout') {
      window.location.href = '/login?expired=true';
    } else if (reason === 'kicked') {
      window.location.href = '/login?kicked=true';
    } else {
      window.location.href = '/login?expired=true';
    }
  },

  // Initialisiert Session-Listener (beim App-Start)
  initSessionListener: () => {
    // Session-Expired Events vom httpClient
    const { sessionExpiredEvent } = require('./adapters/httpClient');
    sessionExpiredEvent.addEventListener('sessionExpired', (event) => {
      console.log('Session expired event:', event.detail);
      authService.handleSessionExpired('kicked');
    });

    // Storage Event - erkennt wenn ein anderer Tab die Session aendert (gleicher Browser)
    window.addEventListener('storage', (event) => {
      if (event.key === 'sessionId' && currentSessionId) {
        if (event.newValue !== currentSessionId) {
          console.log('Session changed in another tab');
          authService.stopAllTimers();
          currentSessionId = null;
          window.location.href = '/login?kicked=true';
        }
      }
    });

    // Wenn bereits eingeloggt (z.B. nach Page-Refresh)
    if (authService.isAuthenticated()) {
      currentSessionId = localStorage.getItem('sessionId');
      authService.startInactivityTimer();
      authService.startSessionCheck();
      authService.registerActivityListeners();

      // Initiale Session-Pruefung
      authService.checkSession();
    }
  },
};

export default authService;
